import { create } from 'zustand';
import { applyTheme } from '../theme/applyTheme';
import { DEFAULT_THEME_ID, themeFor, THEMES } from '../theme/themes';
import type { Theme } from '../theme/palette';

const KEY = 'theme';

/**
 * Which theme the interface is wearing.
 *
 * The choice is a display preference and nothing else — it gates nothing and reveals nothing — so it lives in
 * localStorage, where it survives a restart and is read before the first paint. Anything security-relevant stays out
 * of there (skill aerostores-frontend §8); a colour scheme is not that.
 *
 * A custom theme is one an administrator saved: it arrives as data from the server and is applied the same way, so a
 * department can carry its own colours without a rebuild.
 */
interface ThemeState {
  id: string;
  /** set when the active theme is a saved one rather than a shipped preset */
  custom: Theme | null;
  theme: Theme;
  /** the saved themes an administrator has built, offered in the picker beside the three that ship */
  saved: SavedTheme[];
  choose: (id: string) => void;
  wearCustom: (id: string, theme: Theme) => void;
  setSaved: (saved: SavedTheme[]) => void;
}

/** A theme somebody built: the SPA applies it exactly like one of its own. */
export interface SavedTheme {
  code: string;
  name: string;
  theme: Theme;
}

const stored = (): string => {
  try {
    return localStorage.getItem(KEY) ?? DEFAULT_THEME_ID;
  } catch {
    // a locked-down browser or a private window: the default is a perfectly good answer
    return DEFAULT_THEME_ID;
  }
};

const remember = (id: string): void => {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* nothing to do — the choice simply will not survive the session */
  }
};

const initialId = stored();

export const useTheme = create<ThemeState>((set, get) => ({
  id: initialId,
  custom: null,
  theme: themeFor(initialId),
  saved: [],
  choose: (id) => {
    // a saved theme is applied the same way as one that ships; only where its colours came from differs
    const built = get().saved.find((s2) => s2.code === id);
    const theme = built ? built.theme : themeFor(id);
    applyTheme(theme);
    remember(id);
    set({ id, theme, custom: built ? theme : null });
  },
  wearCustom: (id, theme) => {
    applyTheme(theme);
    remember(id);
    set({ id, theme, custom: theme });
  },
  setSaved: (saved) => {
    set({ saved });
    // the remembered choice may name a theme that only arrived just now
    const built = saved.find((s2) => s2.code === get().id);
    if (built) {
      applyTheme(built.theme);
      set({ theme: built.theme, custom: built.theme });
    }
  },
}));

/**
 * Puts the remembered theme on the page before React renders, so the first paint is already right. The stylesheet's
 * own `:root` carries ADPAD's colours, so there is nothing to do — and nothing to flash — when that is the choice.
 */
export function startTheme(): void {
  if (initialId !== DEFAULT_THEME_ID) applyTheme(themeFor(initialId));
}

/** The shipped themes, for the picker. Saved ones are added to these from the store. */
export const themeOptions = Object.entries(THEMES).map(([id, theme]) => ({ id, name: theme.name, dark: theme.dark }));
